import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import FairPotenzial, { FAIR_BASEN } from '../components/lens/FairPotenzial'
import LoginGate from '../components/common/LoginGate'
import ProGate from '../components/common/ProGate'
import UnscharfeIdeen from '../components/common/UnscharfeIdeen'
import { Fehler, Laden, Leer } from '../components/common/States'
import { useUnterbewertet } from '../hooks/useQualyz'
import { formatDatum, formatDezimal, formatZeitpunkt } from '../lib/lenses'
import { grenzenVon } from '../lib/tarife'
import type { FairBasis, UnterbewertetAnfrage, UnterbewertetTitel } from '../services/qualyz'

/**
 * Unterbewertete Aktien (Kim, 23.10.2026).
 *
 * Titel, deren Kurs unter dem eigenen fairen Wert liegt -- nach denselben
 * Linien wie in der Kursgrafik der Einzelansicht: Durchschnitts-KGV,
 * Durchschnitts-KUV und Durchschnitts-Dividendenrendite dieses Titels. Die
 * Liste ist vorgewaermt (fairwert_liste.py), jede Auswahl kostet nur das
 * Filtern.
 *
 * Gezeigt wird das Potenzial bis zum fairen Wert (Kim, 24.10.2026): "+25 %"
 * heisst, der Kurs muesste um 25 % steigen.
 *
 * Die Standardansicht muss genau zu `fairwert_liste.UNTERBEWERTET_AB_POTENZIAL`
 * und den Defaults von /api/unterbewertet passen -- kostenlose Konten
 * bekommen nur sie.
 */
const STANDARD: UnterbewertetAnfrage = { basen: ['kgv', 'kuv'], alle: true, min_potenzial: 10, min_quality: null }

const POTENZIALE = [10, 25, 50]
const QUALITY = [null, 50, 60, 70]
// Ab hier ist ein Potenzial selten echte Unterbewertung, haeufiger ein Bruch im
// Geschaeft (Fusion, Abspaltung, eingebrochener Gewinn). +150 % entspricht
// einem Kurs 60 % unter dem fairen Wert.
const EXTREM_PCT = 150

type Spalte = 'mittel' | 'titel' | FairBasis | 'quality'

function gleich(a: UnterbewertetAnfrage, b: UnterbewertetAnfrage): boolean {
  return (
    [...a.basen].sort().join() === [...b.basen].sort().join() &&
    a.alle === b.alle &&
    a.min_potenzial === b.min_potenzial &&
    a.min_quality === b.min_quality
  )
}

function wertVon(t: UnterbewertetTitel, spalte: Spalte): number | string | null {
  switch (spalte) {
    case 'titel':
      return t.symbol
    case 'mittel':
      return t.potenzial_mittel_pct
    case 'quality':
      return t.quality
    default: {
      const e = t.basen[spalte]
      return e && !e.ausserhalb ? e.potenzial_pct : null
    }
  }
}

export default function UnterbewertetPage() {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return null
  if (!isAuthenticated) {
    return (
      <div className="q-page">
        <section className="q-card">
          <LoginGate titel="Die unterbewerteten Aktien sind für angemeldete Nutzer." zeilen={7} />
        </section>
      </div>
    )
  }
  return <Ansicht />
}

function Ansicht() {
  const { user } = useAuth()
  const eigeneFilter = grenzenVon(user).eigene_filter
  const [auswahl, setAuswahl] = useState<UnterbewertetAnfrage>(STANDARD)
  // Wie das Backend: Quality zuerst. Nach dem Abstand allein stuenden oben
  // die Brueche im Geschaeft, nicht die Gelegenheiten.
  const [sortierung, setSortierung] = useState<{ spalte: Spalte; absteigend: boolean }>({
    spalte: 'quality',
    absteigend: true,
  })
  const liste = useUnterbewertet(eigeneFilter ? auswahl : STANDARD)

  const titel = liste.data?.titel ?? []
  const gesamt = liste.data?.gesamt ?? titel.length
  const gekuerzt = (liste.data?.begrenzt_auf ?? null) !== null && gesamt > titel.length

  const sortiert = [...titel].sort((a, b) => {
    const wa = wertVon(a, sortierung.spalte)
    const wb = wertVon(b, sortierung.spalte)
    if (wa === null || wb === null) return wa === wb ? 0 : wa === null ? 1 : -1
    const v = typeof wa === 'string' && typeof wb === 'string' ? wa.localeCompare(wb, 'de') : Number(wa) - Number(wb)
    return sortierung.absteigend ? -v : v
  })

  const sortierenNach = (spalte: Spalte) =>
    setSortierung((s) =>
      s.spalte === spalte ? { spalte, absteigend: !s.absteigend } : { spalte, absteigend: spalte !== 'titel' },
    )

  const basisUmschalten = (b: FairBasis) =>
    setAuswahl((a) => {
      const an = a.basen.includes(b)
      if (an && a.basen.length === 1) return a // eine Basis muss bleiben
      const neu = an ? a.basen.filter((x) => x !== b) : [...a.basen, b]
      return { ...a, basen: FAIR_BASEN.map((f) => f.key).filter((k) => neu.includes(k)) }
    })

  const proHinweis = eigeneFilter ? null : (
    <span className="q-pro-badge" title="Eigene Auswahl mit Pro">
      Pro
    </span>
  )

  const Kopf = ({ spalte, label, hinweis, className }: { spalte: Spalte; label: string; hinweis: string; className?: string }) => {
    const aktiv = sortierung.spalte === spalte
    const Pfeil = aktiv ? (sortierung.absteigend ? ArrowDown : ArrowUp) : ArrowUpDown
    return (
      <th scope="col" className={className} aria-sort={aktiv ? (sortierung.absteigend ? 'descending' : 'ascending') : 'none'}>
        <button type="button" className={`q-sort${aktiv ? ' is-active' : ''}`} onClick={() => sortierenNach(spalte)} title={hinweis}>
          {label}
          <Pfeil aria-hidden="true" />
        </button>
      </th>
    )
  }

  return (
    <div className="q-page">
      <section className="q-controls" aria-label="Auswahl">
        <div className="q-cgroup q-cgroup--wide">
          <span className="q-clabel" id="basen-label">
            Fairer Wert nach
            {proHinweis}
          </span>
          <div className="q-togglechips" role="group" aria-labelledby="basen-label">
            {FAIR_BASEN.map((b) => (
              <button
                key={b.key}
                type="button"
                className="q-toggle"
                title={b.lang}
                aria-pressed={auswahl.basen.includes(b.key)}
                onClick={() => basisUmschalten(b.key)}
                disabled={!eigeneFilter}
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>

        <div className="q-cgroup">
          <label className="q-clabel" htmlFor="verknuepfung">
            Gilt
            {proHinweis}
          </label>
          <select
            id="verknuepfung"
            className="q-select"
            value={auswahl.alle ? 'alle' : 'eine'}
            onChange={(e) => setAuswahl((a) => ({ ...a, alle: e.target.value === 'alle' }))}
            disabled={!eigeneFilter || auswahl.basen.length === 1}
          >
            <option value="alle">auf jeder gewählten Basis</option>
            <option value="eine">auf mindestens einer</option>
          </select>
        </div>

        <div className="q-cgroup">
          <label className="q-clabel" htmlFor="potenzial">
            Potenzial mindestens
            {proHinweis}
          </label>
          <select
            id="potenzial"
            className="q-select"
            value={auswahl.min_potenzial}
            onChange={(e) => setAuswahl((a) => ({ ...a, min_potenzial: Number(e.target.value) }))}
            disabled={!eigeneFilter}
          >
            {POTENZIALE.map((w) => (
              <option key={w} value={w}>
                +{formatDezimal(w, 0)} % bis zum fairen Wert
              </option>
            ))}
          </select>
        </div>

        <div className="q-cgroup">
          <label className="q-clabel" htmlFor="quality">
            Quality-Linse
            {proHinweis}
          </label>
          <select
            id="quality"
            className="q-select"
            value={auswahl.min_quality ?? ''}
            onChange={(e) => setAuswahl((a) => ({ ...a, min_quality: e.target.value ? Number(e.target.value) : null }))}
            disabled={!eigeneFilter}
          >
            {QUALITY.map((q) => (
              <option key={q ?? 'alle'} value={q ?? ''}>
                {q === null ? 'alle' : `ab ${q} Punkten`}
              </option>
            ))}
          </select>
        </div>

        {eigeneFilter && !gleich(auswahl, STANDARD) && (
          <div className="q-controls-actions">
            <button type="button" className="q-btn q-btn-ghost q-btn-sm" onClick={() => setAuswahl(STANDARD)}>
              Zurücksetzen
            </button>
          </div>
        )}
      </section>

      <div className="q-note">
        <span className="q-note-mark" aria-hidden="true">
          i
        </span>
        <span>
          <b>Fairer Wert</b> heißt hier: das Durchschnitts-Vielfache <b>dieses</b> Titels über bis zu elf Jahre,
          angewandt auf seine heutigen Zahlen. Dieselben Linien zeigt die Kursgrafik der Einzelansicht.{' '}
          <b>Potenzial +25 %</b> heißt, der Kurs müsste um 25 % steigen, um diesen Wert zu erreichen. Das ist{' '}
          <b>kein Kaufsignal</b>: Günstig gegen die eigene Geschichte ist oft ein Geschäft, das sein altes Vielfache
          nicht mehr verdient. Ein Potenzial über 150 % deutet eher auf einen Bruch hin (Fusion, Abspaltung,
          eingebrochener Gewinn) als auf ein Schnäppchen. Ob die Liste im Rückvergleich einen Vorsprung bringt, ist
          noch nicht gemessen.
        </span>
      </div>

      <section className="q-card" aria-busy={liste.isFetching}>
        {liste.isPending ? (
          <Laden text="Die Liste wird geladen …" />
        ) : liste.isError && !liste.data ? (
          <Fehler fehler={liste.error} nochmal={() => liste.refetch()} />
        ) : liste.data?.wird_berechnet ? (
          <Laden
            text="Die fairen Werte werden gerade berechnet …"
            hinweis="Das dauert nach einer Aktualisierung rund drei Minuten. Die Seite lädt dann von selbst."
          />
        ) : (
          <>
            <div className="q-card-head">
              <p className="q-meta">
                <b>{gesamt}</b> von {liste.data?.bestand ?? '–'} Titeln mit fairem Wert
                {gekuerzt ? ` · die ersten ${titel.length}` : ''}
                {liste.data?.stand ? ` · Kurse vom ${formatDatum(liste.data.stand)}` : ''}
                {liste.data?.berechnet_am ? ` · berechnet ${formatZeitpunkt(liste.data.berechnet_am)}` : ''}
              </p>
            </div>
            {titel.length === 0 ? (
              <Leer text="Kein Titel liegt mit dieser Auswahl unter dem fairen Wert." />
            ) : (
              <div className={`q-table-wrap${liste.isFetching ? ' is-stale' : ''}`}>
                <table className="q-table">
                  <thead>
                    <tr>
                      <th scope="col" className="q-rank">
                        #
                      </th>
                      <Kopf spalte="titel" label="Titel" hinweis="Titel nach Kürzel sortieren" />
                      <th scope="col" className="q-num" title="Letzter Schlusskurs in Handelswährung">
                        Kurs
                      </th>
                      {FAIR_BASEN.map((b) => (
                        <Kopf
                          key={b.key}
                          spalte={b.key}
                          label={b.label}
                          hinweis={`${b.label}: Potenzial nach ${b.lang} sortieren`}
                          className="q-num"
                        />
                      ))}
                      <Kopf
                        spalte="mittel"
                        label="Mittel"
                        hinweis="Mittel: mittleres Potenzial der gewählten Basen"
                        className="q-num"
                      />
                      <Kopf spalte="quality" label="Quality" hinweis="Nach Punkten der Quality-Linse sortieren" className="q-num" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortiert.map((t, i) => (
                      <tr key={t.symbol}>
                        <td className="q-rank">{i + 1}</td>
                        <td>
                          <Link
                            className="q-titlelink"
                            to={`/aktie/${encodeURIComponent(t.symbol)}`}
                            title={`${t.symbol} im Detail ansehen`}
                          >
                            <div className="q-titlecell">
                              <span className="q-sym">
                                {t.symbol}
                                {t.datenhinweise.length > 0 && (
                                  <span className="q-hint" title={`Datenhinweis: ${t.datenhinweise.join(' · ')}`}>
                                    {' '}
                                    <AlertTriangle aria-label={`Datenhinweis: ${t.datenhinweise.join(' · ')}`} />
                                  </span>
                                )}
                              </span>
                              <span className="q-name" title={t.name}>
                                {t.name}
                              </span>
                              {t.sector && <span className="q-sector">{t.sector}</span>}
                            </div>
                          </Link>
                        </td>
                        <td className="q-num">{formatDezimal(t.kurs, 2)}</td>
                        {FAIR_BASEN.map((b) => {
                          const e = t.basen[b.key]
                          const grundlage = e
                            ? b.key === 'dividende'
                              ? `Ø-Rendite ${formatDezimal(e.rendite_pct, 2)} %, fairer Wert ${formatDezimal(e.fair, 2)}`
                              : `${b.label} ${formatDezimal(e.vielfaches, 1)}, fairer Wert ${formatDezimal(e.fair, 2)}`
                            : `Kein fairer Wert nach ${b.lang}`
                          return (
                            <td key={b.key} className="q-num">
                              <FairPotenzial potenzial={e?.potenzial_pct} ausserhalb={e?.ausserhalb} titel={grundlage} />
                            </td>
                          )
                        })}
                        <td className="q-num">
                          <FairPotenzial
                            potenzial={t.potenzial_mittel_pct}
                            titel={t.potenzial_mittel_pct >= EXTREM_PCT ? 'Sehr großes Potenzial, Einzelansicht prüfen' : 'Mittel der gewählten Basen'}
                          />
                        </td>
                        <td className="q-num">{formatDezimal(t.quality, 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {gekuerzt && <ProGate titel={`Alle ${gesamt} Titel mit Pro`} vorschau={<UnscharfeIdeen anzahl={4} />} />}
          </>
        )}
      </section>
    </div>
  )
}
